import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
import { Button, Box, Container, Typography, Stack, Theme, useTheme, Grid } from '@mui/material';
import { varFadeIn, varFadeInUp, varWrapEnter, varFadeInRight } from '../../animate';
import { ButtonContentProps } from '../../../@types/components-overview';
import { SxProps } from '@mui/system';

// ----------------------------------------------------------------------
interface RootStyleProps {
  theme: Theme;
  position: string;
}

const RootStyle = styled(motion.div)(({ theme, position = 'fixed' }: RootStyleProps) => ({
  height: '100vh',
  alignItems: 'left',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    position: position,
    top: 0,
    left: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    height: '125vh'
  }
}));

const HeroOverlayStyle = styled(motion.img)(({ theme }) => ({
  zIndex: 9,
  width: '100%',
  height: '100vh',
  objectFit: 'cover',
  position: 'absolute',
  [theme.breakpoints.down('sm')]: {
    height: '125vh'
  }
}));

const HeroImgStyle = styled(motion.img)(({ theme }) => ({
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: 8,
  width: '100%',
  margin: 'auto',
  position: 'absolute',
  height: '100%',
  [theme.breakpoints.up('xs')]: {
    objectPosition: '68%',
    objectFit: 'cover'
  },
  [theme.breakpoints.down('sm')]: {
    height: '125vh'
  }
}));

const ContainerResponsive = styled(Container)(({ theme }) => ({
  zIndex: 10,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    top: '45%',
    textAlign: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center'
  }
}));
// ----------------------------------------------------------------------

export interface LandingHeroProps {
  title?: string;
  titlePrimary?: string;
  subtitle?: string;
  text?: string;
  minimalText?: string;
  image: string;
  overlay: string;
  buttons?: Array<ButtonContentProps>;
  position?: string;
  sx?: SxProps;
}

export default function LandingHero({
  text,
  titlePrimary,
  subtitle,
  buttons,
  title,
  minimalText,
  image,
  overlay,
  position = 'fixed',
  sx
}: LandingHeroProps) {
  const theme = useTheme();
  return (
    <>
      <RootStyle
        sx={sx}
        position={position}
        theme={theme}
        initial="initial"
        animate="animate"
        variants={varWrapEnter}
      >
        <HeroOverlayStyle alt="overlay" src={overlay} variants={varFadeIn} />

        <HeroImgStyle alt="hero" src={image} variants={varFadeInUp} />
        <ContainerResponsive maxWidth="lg">
          <Grid container>
            <Grid item lg={6} xl={6} md={6} sm={12} xs={12}>
              <Stack spacing={3}>
                {minimalText && (
                  <motion.div variants={varFadeInRight}>
                    <Typography variant={'overline'} sx={{ color: 'primary.main' }}>
                      {minimalText}
                    </Typography>
                  </motion.div>
                )}
                {title && (
                  <motion.div variants={varFadeInRight}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: 'common.white',
                        display: {
                          xl: 'block',
                          lg: 'block',
                          md: 'block',
                          sm: 'none',
                          xs: 'none'
                        }
                      }}
                    >
                      {title}
                      {titlePrimary && (
                        <Typography variant="h1" style={{ color: theme.palette.primary.main }}>
                          {titlePrimary}
                        </Typography>
                      )}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'common.white',
                        display: {
                          xl: 'none',
                          lg: 'none',
                          md: 'none',
                          sm: 'block',
                          xs: 'block'
                        }
                      }}
                    >
                      {title}
                      {titlePrimary && (
                        <Typography variant="h3" style={{ color: theme.palette.primary.main }}>
                          {titlePrimary}
                        </Typography>
                      )}
                    </Typography>
                  </motion.div>
                )}
                {subtitle && (
                  <motion.div variants={varFadeInRight}>
                    <Stack direction={'column'} spacing={2}>
                      <Typography
                        sx={{
                          color: 'common.white',
                          fontWeight: 'bold'
                        }}
                      >
                        {subtitle}
                      </Typography>
                      <Typography sx={{ color: 'common.white' }}>{text}</Typography>
                    </Stack>
                  </motion.div>
                )}
                {buttons && (
                  <motion.div variants={varFadeInRight}>
                    {buttons.length > 0 && (
                      <Stack
                        direction={{
                          sm: 'column',
                          xs: 'column',
                          md: 'row',
                          lg: 'row',
                          xl: 'row'
                        }}
                        spacing={3}
                      >
                        {buttons.map((button) =>
                          button.href !== '' ? (
                            <Button
                              key={buttons.indexOf(button)}
                              size={button.size}
                              href={button.href}
                              color={button.color}
                              variant={button.variant}
                            >
                              {button.text}
                            </Button>
                          ) : (
                            <Button
                              key={buttons.indexOf(button)}
                              size={button.size}
                              color={button.color}
                              variant={button.variant}
                              component={RouterLink}
                              to={button.to}
                            >
                              {button.text}
                            </Button>
                          )
                        )}
                      </Stack>
                    )}
                  </motion.div>
                )}
              </Stack>
            </Grid>
          </Grid>
        </ContainerResponsive>
      </RootStyle>
      {position === 'fixed' && <Box sx={{ height: { md: '100vh' } }} />}
    </>
  );
}
